"use client";

// ============================================================
// CLUBHUB – Der Gruppenbaum als Liste
// ============================================================
// Abteilungen mit ihren Mannschaften darunter, eingerueckt. Auf
// breiten Schirmen die Listentabelle des Agent (ganze Zeile oeffnet,
// Spaltenkoepfe sortieren die Geschwister); auf dem Telefon je
// oberste Gruppe EINE Karte mit ihren Untergruppen darin — dort
// braucht niemand Spalten, sondern Name, Zahl und wer leitet.
//
// Gezaehlt wird, wer HEUTE dabei ist, und zwar je Person einmal:
// die Jugend zaehlt ihre U13 und U17 mit, aber niemanden doppelt.
// ============================================================
import { useMemo } from "react";
import Link from "next/link";
import { CornerDownRight, Mail, UsersRound } from "lucide-react";
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui";
import {
  Listenkoerper,
  Listentabelle,
  SortableHead,
  SortableHeaderRow,
  Textzelle,
  UEBER_DER_ZEILE,
  Zeilenverweis,
} from "@/components/liste";
import { GRUPPEN_DEFAULT_SORT, GRUPPEN_SORT, type GruppenSortKey } from "@/lib/gruppen/ansicht";
import { funktionsRang, mitgliederzahl, pfadText, stand, type BaumZeile, type Mitgliederzahl } from "@/lib/gruppen/logik";
import type { GruppeView } from "@/lib/gruppen/views";
import type { SortState } from "@/lib/sorting";
import { cn } from "@/lib/utils";
import { useGruppenBestand } from "./gruppen-bestand";
import { GruppenMenue } from "./gruppen-menue";
import { ArtText, GruppenMarke } from "./zeichen";

export type GruppenKennzahlen = {
  zahl: Map<string, Mitgliederzahl>;
  leitung: Map<string, { name: string; funktion: string }>;
};

/** Mitgliederzahl und erstes Amt je Gruppe — einmal fuer alle Zeilen. */
export function useGruppenKennzahlen(): GruppenKennzahlen {
  const { gruppen, zugehoerigkeiten, personen, heute } = useGruppenBestand();
  return useMemo(() => {
    const zahl = new Map<string, Mitgliederzahl>();
    const leitung = new Map<string, { name: string; funktion: string }>();
    for (const g of gruppen) {
      zahl.set(g.id, mitgliederzahl(gruppen, zugehoerigkeiten, g.id, heute));
      const amt = zugehoerigkeiten
        .filter((z) => z.groupId === g.id && z.function && stand(z, heute) === "aktuell")
        .sort((a, b) => funktionsRang(a.function) - funktionsRang(b.function))[0];
      if (amt?.function) leitung.set(g.id, { name: personen[amt.contactId]?.displayName ?? "", funktion: amt.function });
    }
    return { zahl, leitung };
  }, [gruppen, zugehoerigkeiten, personen, heute]);
}

/** Die Zahl in der Zeile: alle (mit Untergruppen), darunter, wie viele direkt. */
function Zahl({ z }: { z: Mitgliederzahl | undefined }) {
  if (!z) return null;
  return (
    <span className="numeric block">
      <span className={cn(z.gesamt === 0 && "text-text-tertiary")}>{z.gesamt}</span>
      {z.gesamt !== z.direkt && <span className="text-caption text-text-tertiary block">{z.direkt} direkt</span>}
    </span>
  );
}

export function GruppenBaum({
  zeilen,
  sort,
  zeigePfad = false,
  onBearbeiten,
  onUntergruppe,
}: {
  zeilen: BaumZeile[];
  /** Ohne: Spaltenkoepfe sortieren nicht (Untergruppen im Gruppenblatt). */
  sort?: SortState<GruppenSortKey>;
  /** Statt Einrueckung den Weg als Zeile darunter (Archiv). */
  zeigePfad?: boolean;
  onBearbeiten: (g: GruppeView) => void;
  onUntergruppe: (g: GruppeView) => void;
}) {
  const { gruppen } = useGruppenBestand();
  const { zahl, leitung } = useGruppenKennzahlen();

  const koepfe = sort ? (
    <SortableHeaderRow sort={sort} defaultColumn={GRUPPEN_DEFAULT_SORT}>
      <SortableHead column="gruppe" {...GRUPPEN_SORT.gruppe}>GRUPPE</SortableHead>
      <SortableHead column="art" {...GRUPPEN_SORT.art}>ART</SortableHead>
      <SortableHead column="mitglieder" {...GRUPPEN_SORT.mitglieder} align="right">MITGLIEDER</SortableHead>
      <TableHead>LEITUNG</TableHead>
      <TableHead>VEREINS-MAIL</TableHead>
      <TableHead className="w-10" aria-label="Aktionen" />
    </SortableHeaderRow>
  ) : (
    <TableRow>
      <TableHead>GRUPPE</TableHead>
      <TableHead>ART</TableHead>
      <TableHead className="text-right">MITGLIEDER</TableHead>
      <TableHead>LEITUNG</TableHead>
      <TableHead>VEREINS-MAIL</TableHead>
      <TableHead className="w-10" aria-label="Aktionen" />
    </TableRow>
  );

  // Auf dem Telefon: je oberste Gruppe eine Karte.
  const karten: BaumZeile[][] = [];
  for (const z of zeilen) {
    if (z.tiefe === 0 || karten.length === 0) karten.push([z]);
    else karten[karten.length - 1]!.push(z);
  }

  return (
    <>
      <Listentabelle viewportClassName="max-md:hidden">
        <TableHeader>{koepfe}</TableHeader>
        <TableBody>
          {zeilen.map(({ gruppe: g, tiefe, nurPfad }) => {
            const l = leitung.get(g.id);
            return (
              <TableRow key={g.id} className={cn("border-border-subtle border-b", nurPfad && "opacity-60")}>
                <TableCell className="w-full max-w-0">
                  <div className="flex min-w-0 items-center gap-2.5" style={{ paddingLeft: zeigePfad ? 0 : tiefe * 22 }}>
                    {tiefe > 0 && !zeigePfad && (
                      <CornerDownRight className="text-text-tertiary -ml-1 h-3.5 w-3.5 shrink-0" aria-hidden />
                    )}
                    <GruppenMarke kind={g.kind} farbe={g.color} groesse="sm" />
                    <div className="min-w-0">
                      <Zeilenverweis href={`/gruppen/${g.id}`} className="block truncate">
                        {g.name}
                      </Zeilenverweis>
                      <p className="text-caption text-text-tertiary truncate">
                        {zeigePfad && g.parentId ? `${pfadText(gruppen, g.parentId)} · ` : ""}
                        {g.description ?? (g.sportCode ? g.sportCode : "")}
                      </p>
                    </div>
                  </div>
                </TableCell>
                <TableCell>
                  <ArtText kind={g.kind} />
                </TableCell>
                <TableCell className="text-right">
                  <Zahl z={zahl.get(g.id)} />
                </TableCell>
                <TableCell className="whitespace-nowrap">
                  {l ? (
                    <>
                      <p className="max-w-48 truncate">{l.name}</p>
                      <p className="text-caption text-text-tertiary max-w-48 truncate">{l.funktion}</p>
                    </>
                  ) : (
                    <span className="text-text-tertiary">–</span>
                  )}
                </TableCell>
                <Textzelle
                  className="max-w-64"
                  text={
                    g.emailAlias ? (
                      <a href={`mailto:${g.emailAlias}`} className={cn(UEBER_DER_ZEILE, "text-text-secondary hover:text-accent")} title={g.emailAlias}>
                        {g.emailAlias}
                      </a>
                    ) : (
                      <span className="text-text-tertiary">–</span>
                    )
                  }
                />
                <TableCell className="w-10 pr-2">
                  <div className={UEBER_DER_ZEILE}>
                    <GruppenMenue gruppe={g} onBearbeiten={() => onBearbeiten(g)} onUntergruppe={() => onUntergruppe(g)} mitOeffnen />
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Listentabelle>

      <Listenkoerper className="space-y-3 p-3 md:hidden">
        {karten.map((karte) => (
          <section key={karte[0]!.gruppe.id} className="bg-bg-primary border-border divide-border-subtle divide-y overflow-hidden rounded-xl border">
            {karte.map(({ gruppe: g, tiefe, nurPfad }) => {
              const l = leitung.get(g.id);
              const z = zahl.get(g.id);
              return (
                <div key={g.id} className={cn("relative flex items-start gap-3 px-3 py-3", nurPfad && "opacity-60")} style={{ paddingLeft: 12 + (zeigePfad ? 0 : tiefe * 18) }}>
                  <GruppenMarke kind={g.kind} farbe={g.color} groesse={tiefe === 0 ? "md" : "sm"} />
                  <div className="min-w-0 flex-1">
                    <Link href={`/gruppen/${g.id}`} className="text-body-sm text-text-primary block truncate font-medium after:absolute after:inset-0 after:content-['']">
                      {g.name}
                    </Link>
                    <p className="text-caption text-text-tertiary truncate">
                      {zeigePfad && g.parentId ? `${pfadText(gruppen, g.parentId)} · ` : ""}
                      <ArtText kind={g.kind} className="text-text-tertiary inline-flex" />
                    </p>
                    {l && (
                      <p className="text-caption text-text-secondary mt-1 truncate">
                        {l.funktion} · {l.name}
                      </p>
                    )}
                    {g.emailAlias && (
                      <p className="text-caption text-text-tertiary mt-0.5 flex items-center gap-1 truncate">
                        <Mail className="h-3 w-3 shrink-0" aria-hidden />
                        <span className="truncate">{g.emailAlias}</span>
                      </p>
                    )}
                  </div>
                  <div className="flex shrink-0 items-center gap-1">
                    <span className="numeric text-body-sm text-text-primary inline-flex items-center gap-1" title={`${z?.gesamt ?? 0} Personen`}>
                      <UsersRound className="text-text-tertiary h-3.5 w-3.5" aria-hidden />
                      {z?.gesamt ?? 0}
                      <span className="sr-only">Personen</span>
                    </span>
                    <div className={UEBER_DER_ZEILE}>
                      <GruppenMenue gruppe={g} onBearbeiten={() => onBearbeiten(g)} onUntergruppe={() => onUntergruppe(g)} mitOeffnen />
                    </div>
                  </div>
                </div>
              );
            })}
          </section>
        ))}
      </Listenkoerper>
    </>
  );
}
